/**
 * MobileStatusBar — single-row 44px status header for the mobile Play
 * layout.
 *
 * Compressed status: round/turn pill, a small `?` phase-info button (new
 * in Stream G2 — restores parity with desktop StatusBar's PhaseInfoButton
 * tooltip), current player color dot + first name, and a `⋮` button that
 * opens a small popover with the session-code copy action. We drop the
 * always-visible session-code chip because it dominates a 390px row.
 *
 * The phase chip itself stays removed — three of the four phases happen
 * invisibly between the player's interactive moments under simplified-
 * play autopilot, so the chip flashed meaningless labels. PhaseCoach
 * beneath this bar carries the plain-English "what to do now"
 * instruction; the new `?` button surfaces the deeper "what is this
 * phase actually for?" explanation on demand without cluttering the
 * row when the player isn't asking.
 */

import { forwardRef, type ReactNode, useCallback, useRef, useState } from 'react';
import { AnimatePresence } from 'motion/react';
import type { Player, PlayerColorKey } from '@qd/protocol';
import { fontStacks, playerColors, surfaces } from '@qd/design-tokens';
import { formatTimerMMSS, useTurnTimer } from '../../../hooks/useTurnTimer';
import { useTurnPhase } from '../../../stores/gameStore';
import { MobilePhaseInfoPopover } from './MobilePhaseInfoPopover';

export const MOBILE_STATUS_BAR_HEIGHT = 44;

interface MobileStatusBarProps {
  readonly roundNumber: number;
  readonly turnNumber: number;
  readonly currentPlayerIdx: number;
  readonly currentPlayer: Player | null;
  readonly sessionId: string;
  readonly turnTimerSeconds: number;
}

function hexAlpha(hex: string, alpha: number): string {
  const m = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (!m) return hex;
  const r = parseInt(m[1]!, 16);
  const g = parseInt(m[2]!, 16);
  const b = parseInt(m[3]!, 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function MobileStatusBar({
  roundNumber,
  turnNumber,
  currentPlayerIdx,
  currentPlayer,
  sessionId,
  turnTimerSeconds,
}: MobileStatusBarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  // Phase-info popover state. We snapshot the trigger's bounding rect at
  // toggle time (rather than on every render) so the popover anchor
  // position is stable even if the row reflows underneath it.
  const [phaseInfoRect, setPhaseInfoRect] = useState<DOMRect | null>(null);
  const phaseInfoButtonRef = useRef<HTMLButtonElement | null>(null);
  const phase = useTurnPhase();

  const togglePhaseInfo = useCallback(() => {
    setPhaseInfoRect((current) => {
      if (current !== null) return null;
      const rect = phaseInfoButtonRef.current?.getBoundingClientRect();
      return rect ?? null;
    });
  }, []);
  const closePhaseInfo = useCallback(() => setPhaseInfoRect(null), []);

  return (
    <div
      role="status"
      aria-label="Match status"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 8,
        height: MOBILE_STATUS_BAR_HEIGHT,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        padding: '0 10px',
        background: surfaces.paper,
        borderBottom: `1.5px dashed ${surfaces.waxRed}`,
        color: surfaces.ink,
        fontFamily: fontStacks.body,
        fontSize: 12,
      }}
    >
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontFamily: fontStacks.mono,
            fontSize: 10,
            letterSpacing: '0.1em',
            color: surfaces.ink,
            opacity: 0.85,
          }}
        >
          R{roundNumber}·T{turnNumber}
        </span>
        <PhaseInfoButton
          ref={phaseInfoButtonRef}
          open={phaseInfoRect !== null}
          disabled={phase === null}
          onToggle={togglePhaseInfo}
        />
      </div>

      <MobileTimerPill
        turnTimerSeconds={turnTimerSeconds}
        turnNumber={turnNumber}
        currentPlayerIdx={currentPlayerIdx}
      />

      <CurrentPlayerBadge player={currentPlayer} />

      <MenuButton open={menuOpen} onToggle={() => setMenuOpen((v) => !v)} />

      {menuOpen ? (
        <SessionCodeMenu sessionId={sessionId} onClose={() => setMenuOpen(false)} />
      ) : null}

      <AnimatePresence>
        {phaseInfoRect !== null && phase !== null ? (
          <MobilePhaseInfoPopover
            key="phase-info"
            phase={phase}
            anchorRect={phaseInfoRect}
            onClose={closePhaseInfo}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
}

function MobileTimerPill({
  turnTimerSeconds,
  turnNumber,
  currentPlayerIdx,
}: {
  turnTimerSeconds: number;
  turnNumber: number;
  currentPlayerIdx: number;
}): ReactNode {
  const { enabled, remainingSec } = useTurnTimer({
    durationSec: turnTimerSeconds,
    turnNumber,
    currentPlayerIdx,
  });
  if (!enabled) return null;
  const urgent = remainingSec <= 10;
  const accent = urgent ? surfaces.waxRed : surfaces.ink;
  return (
    <span
      role="timer"
      aria-label={`Turn timer: ${remainingSec}s`}
      style={{
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 7px',
        border: `1.2px ${urgent ? 'solid' : 'dashed'} ${accent}`,
        borderRadius: 2,
        background: urgent
          ? hexAlpha(surfaces.waxRed, remainingSec === 0 ? 0.28 : 0.16)
          : 'transparent',
        color: accent,
        fontFamily: fontStacks.mono,
        fontSize: 11,
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        letterSpacing: '0.06em',
      }}
    >
      ⏱ {formatTimerMMSS(remainingSec)}
    </span>
  );
}

function CurrentPlayerBadge({ player }: { player: Player | null }): ReactNode {
  if (!player) {
    return (
      <span
        style={{
          fontFamily: fontStacks.mono,
          fontSize: 10,
          color: surfaces.ink,
          opacity: 0.55,
          flex: 1,
          textAlign: 'right',
        }}
      >
        WAITING…
      </span>
    );
  }
  const swatch = playerColors[player.color as PlayerColorKey].hex;
  // Trim long names so the row stays tight; first 12 chars covers most
  // single-word names without an ellipsis cliff.
  const displayName = player.name.length > 12 ? `${player.name.slice(0, 11)}…` : player.name;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        flex: 1,
        justifyContent: 'flex-end',
        minWidth: 0,
      }}
    >
      <span
        aria-hidden
        style={{
          display: 'inline-block',
          width: 10,
          height: 10,
          borderRadius: 2,
          background: swatch,
          border: `1px solid ${surfaces.ink}`,
          opacity: 0.95,
          flexShrink: 0,
        }}
      />
      <span
        style={{
          fontWeight: 700,
          fontSize: 12,
          color: surfaces.ink,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {displayName}
      </span>
    </span>
  );
}

/**
 * PhaseInfoButton — small circular `?` next to the round/turn pill.
 *
 * Visible target is 28x28 (per design constraints), but we wrap the
 * button in 8px of horizontal padding via the surrounding flex gap so
 * the effective hit zone exceeds the 44x44 minimum recommended for
 * thumb-driven taps. Disabled when phase is null (pre-game / between
 * matches) so we never open a popover that has nothing meaningful to
 * say.
 *
 * Visual style: chalk-rough circle with pencil-graphite border and a
 * subtle slate-grey fill, swapping to a mustard tint when open so the
 * player can see the trigger latched while reading the popover.
 */
const PhaseInfoButton = forwardRef<
  HTMLButtonElement,
  { open: boolean; disabled: boolean; onToggle: () => void }
>(function PhaseInfoButton({ open, disabled, onToggle }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-label="What is this phase?"
      aria-expanded={open}
      title="What is this phase?"
      style={{
        flexShrink: 0,
        width: 28,
        height: 28,
        padding: 4,
        borderRadius: '50%',
        border: `1.2px solid #3A3833`,
        background: open
          ? hexAlpha(playerColors.mustard.hex, 0.32)
          : hexAlpha(surfaces.slate, 0.12),
        color: '#3A3833',
        fontFamily: fontStacks.display,
        fontSize: 14,
        fontWeight: 700,
        lineHeight: 1,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background-color 120ms ease',
      }}
    >
      ?
    </button>
  );
});

function MenuButton({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label="More options"
      aria-expanded={open}
      style={{
        flexShrink: 0,
        width: 32,
        height: 32,
        borderRadius: 2,
        border: `1.2px solid ${surfaces.ink}`,
        background: open
          ? hexAlpha(playerColors.mustard.hex, 0.32)
          : hexAlpha(playerColors.mustard.hex, 0.16),
        color: surfaces.ink,
        fontSize: 18,
        lineHeight: 1,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
      }}
    >
      ⋮
    </button>
  );
}

function SessionCodeMenu({
  sessionId,
  onClose,
}: {
  readonly sessionId: string;
  readonly onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    void navigator.clipboard.writeText(sessionId).then(() => {
      setCopied(true);
      window.setTimeout(() => {
        setCopied(false);
        onClose();
      }, 900);
    });
  }, [sessionId, onClose]);

  return (
    <div
      role="dialog"
      aria-label="Session code"
      style={{
        position: 'absolute',
        top: MOBILE_STATUS_BAR_HEIGHT + 4,
        right: 8,
        zIndex: 30,
        background: surfaces.slate,
        border: `1px solid ${surfaces.chalk}`,
        borderRadius: 4,
        padding: '10px 12px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        minWidth: 200,
      }}
    >
      <div
        style={{
          fontFamily: fontStacks.mono,
          fontSize: 9,
          letterSpacing: '0.14em',
          color: surfaces.chalk,
          opacity: 0.6,
          marginBottom: 4,
        }}
      >
        SESSION CODE
      </div>
      <div
        style={{
          fontFamily: fontStacks.mono,
          fontSize: 14,
          letterSpacing: '0.12em',
          color: surfaces.chalk,
          marginBottom: 10,
          wordBreak: 'break-all',
        }}
      >
        {sessionId}
      </div>
      <button
        type="button"
        onClick={handleCopy}
        style={{
          width: '100%',
          padding: '8px 10px',
          background: 'transparent',
          border: `1px dashed ${surfaces.chalk}`,
          color: surfaces.chalk,
          fontFamily: fontStacks.mono,
          fontSize: 11,
          letterSpacing: '0.14em',
          cursor: 'pointer',
          borderRadius: 2,
        }}
      >
        {copied ? 'COPIED ✓' : 'COPY CODE'}
      </button>
    </div>
  );
}
